import { effect, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from "rxjs";
import {
  Customer,
  CustomerMainDataInterface,
  CustomerOrderPreferencesInterface,
  CustomerPreferenceInterface,
  CustomerResponseInterface,
  PostalAddressInterface
} from "../models/customer";
import { HttpCustomerService } from "./http/customer.service";
import { LocalstorageService } from "@gea-waldviertler-website/shop-lib";
import { OrderPersistedInterface } from "../models/order";
import { AuthService } from "./auth.service";
import { CartInterface, CartItem } from "../models/cart";
import { CartService } from "./cart.service";
import { v4 as uuidv4 } from "uuid";

@Injectable({
  providedIn: 'root'
})
export class CustomerService {

  private customerSignal: WritableSignal<Customer | undefined> = signal(undefined)

  preferences?: CustomerPreferenceInterface
  preferenceSubject: BehaviorSubject<CustomerPreferenceInterface[] | undefined>
  availablePreferencesSubject: BehaviorSubject<CustomerPreferenceInterface[] | undefined>

  private authService = inject(AuthService)
  private storage = inject(LocalstorageService)
  private httpService = inject(HttpCustomerService)
  private cartService = inject(CartService)

  constructor(
  ) {
    this.preferenceSubject = new BehaviorSubject<CustomerPreferenceInterface[] | undefined>(undefined)
    this.availablePreferencesSubject = new BehaviorSubject<CustomerPreferenceInterface[] | undefined>(undefined)

    const c = this.storage.get('customer') as CustomerResponseInterface
    if (c) {
      let customer = new Customer(c)
      this.customerSignal.set(customer)
    } else {
      this.customerSignal.set(undefined)
    }

    this.authService.loginEvent.subscribe(user => {
      this.loadCustomer(true)
    })

    this.authService.logoutEvent.subscribe(() => {
      this.clear()
    })

    // save cart to server on cart change if customer is given
    effect(() => {
      const cu = this.customer()
      const ca = this.cartService.cart()
      if (cu && ca) {
        this.saveCart(ca).subscribe((r) => {
        }, (r) => {
          // console.warn(r)
        })
      }
    })

  }

  public get customer(): Signal<Customer | undefined> {
    return this.customerSignal.asReadonly()
  }

  getHttpService() {
    return this.httpService
  }

  deleteAddress(address: PostalAddressInterface) {
    return new Observable<boolean>(subscriber => {
      this.httpService.deleteAddress(address).subscribe(response => {
        subscriber.next(true)
        this.loadCustomer()
      }, () => subscriber.error())
    })
  }

  saveAddress(address: PostalAddressInterface) {
    return new Observable<PostalAddressInterface>(subscriber => {
      this.httpService.saveAddress(address).subscribe(response => {
        subscriber.next(response)
        this.loadCustomer()
      }, () => subscriber.error())
    })
  }

  setDefaultAddress(address: PostalAddressInterface | null, type: 'shipping' | 'billing') {
    return new Observable<boolean>(subscriber => {
      let op = this.customer()?.orderPreferences
      if (! op) {
        subscriber.error()
        return
      }
      if (type == "shipping") {
        op.defaultAddressShippingId = address ? address.id : null
      } else {
        op.defaultAddressBillingId = address ? address.id : null
      }

      this.saveOrderPreferences(op).subscribe(r => {
        subscriber.next(true)
      }, () => subscriber.error())
    })
  }

  getCoupons() {
    return this.httpService.getCoupons()
  }

  savePreferences(p: Map<number, CustomerPreferenceInterface>) {
    return this.httpService.savePreferences(Array.from(p.entries()))
  }

  getAvailablePreferences() {
    if (this.availablePreferencesSubject.getValue() === undefined) {
      this.httpService.getAvailablePreferences().subscribe(prefs => {
        this.availablePreferencesSubject.next(prefs)
      })
    }
    return this.availablePreferencesSubject
  }

  getOrders() {
    let subject = new Subject<OrderPersistedInterface[]>()
    let neworders: OrderPersistedInterface[] = []

    this.httpService.getOrders().subscribe(orders => {
      for (const o of orders) {
        let items: CartItem[] = []

        for (const ci of o.cart.items) {
          items.push(new CartItem(ci))
        }

        let cart: CartInterface = {
          ... o.cart,
          items: items
        }

        neworders.push({
          ... o,
          cart: cart,
          taxesByRate: this.cartService.getTaxesByRate(o.addressShipping?.country, o.shippingMethod?.price, cart)
        })
      }
      subject.next(neworders)
    })

    return subject
  }

  loadCustomer(loadCart = false) {
    let observer = this.httpService.getCustomer()
    observer.subscribe(response => {
      if (response.cart && loadCart) {
        // on new customer, set uuid if not given yet
        if (! response.cart.uuid) {
          response.cart.uuid = uuidv4()
        }
        this.cartService.merge(CartService.getFromResponse(response.cart))
      }
      this.storage.set('customer', response.customer)
      this.customerSignal.set(new Customer(response.customer))
    }, error => {
      this.clear()
    })
  }

  clear(): void {
    this.storage.remove('customer')
    this.customerSignal.set(undefined)
  }

  saveOrderPreferences(orderPreferences: CustomerOrderPreferencesInterface) {
    let obs = new Observable<boolean>(subscriber => {
      this.httpService.saveOrderPreferences(orderPreferences).subscribe(response => {
        subscriber.next(true)
        this.loadCustomer()
      }, () => subscriber.error())
    })

    return obs
  }

  saveMainData(data: CustomerMainDataInterface) {
    let obs = new Observable<boolean>(subscriber => {
      this.httpService.saveCustomerData(data).subscribe(response => {
        this.loadCustomer()
        subscriber.next(true)
      }, () => {
        subscriber.error()
      })
    })

    return obs
  }

  saveCart(cart: CartInterface): Observable<any> {
    return this.httpService.saveCart(cart)
  }
}
